import React, { useEffect, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./QuillEditor.css"; // We'll add a custom CSS file for our theme
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Row,
  Col,
  Card,
  ProgressBar,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage,
  faExpand,
  faTrash,
  faPlus,
  faFileAlt,
  faFilePdf,
  faFileImage,
  faFileCsv,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileCode,
  faFileArchive,
  faFile,
  faUpload,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import toast from "../utils/toastUtils";
import { useAuth } from "../context/AuthContext";
import {
  uploadFile,
  getMeetingFiles,
  getFileDataUrl,
  deleteFile,
} from "../api/api";

const QuillEditor = ({
  value,
  onChange,
  placeholder,
  modules,
  formats,
  readOnly,
  meetingId,
  connectionStatus,
}) => {
  const { currentUser } = useAuth();
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const selectionRef = useRef(null);
  const isTypingRef = useRef(false);
  const typingTimerRef = useRef(null);
  const editorContainerRef = useRef(null);

  // Completely cleanup Quill editor with all related elements
  const cleanupQuill = () => {
    // Clean up any event listeners
    if (quillRef.current) {
      quillRef.current.off("text-change");
      quillRef.current.off("selection-change");
      quillRef.current = null;
    }

    // Clean up any pending timers
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    // Remove all Quill-related DOM elements
    if (editorContainerRef.current) {
      // This is a safer way to completely remove Quill's DOM elements
      editorContainerRef.current.innerHTML = "";
    }
  };

  // Initialize Quill editor
  useEffect(() => {
    // Don't initialize if we don't have a container to mount to
    if (!editorContainerRef.current) return;

    // Clean up any existing Quill instance to avoid duplicates
    cleanupQuill();

    // Create a new container element
    const editorElement = document.createElement("div");
    editorContainerRef.current.appendChild(editorElement);

    // Import the required modules for color and background
    // Make sure Quill knows about these modules
    if (window.Quill) {
      // Register attributors for background and color
      const BackgroundStyle = Quill.import("attributors/style/background");
      const ColorStyle = Quill.import("attributors/style/color");
      Quill.register(BackgroundStyle, true);
      Quill.register(ColorStyle, true);

      // Add the color and background modules if they don't exist
      if (!window.Quill.imports["modules/colorSyntax"]) {
        window.Quill.register("modules/colorSyntax", true);
      }
    }

    // Create custom toolbar options
    const customModules = {
      ...modules,
      toolbar: {
        container: modules?.toolbar || [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ color: [] }],
          [{ background: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          // Custom color handlers for more reliability
          color: function (value) {
            // Get current selection or insert at cursor
            const range = this.quill.getSelection(true);
            // If user has highlighted text, format it; otherwise do nothing
            if (range) {
              if (range.length > 0) {
                this.quill.formatText(
                  range.index,
                  range.length,
                  "color",
                  value
                );
              } else {
                // If no selection, apply to next character typed
                this.quill.format("color", value);
              }
            }
          },
          background: function (value) {
            // Get current selection or insert at cursor
            const range = this.quill.getSelection(true);
            // If user has highlighted text, format it; otherwise do nothing
            if (range) {
              if (range.length > 0) {
                this.quill.formatText(
                  range.index,
                  range.length,
                  "background",
                  value
                );
              } else {
                // If no selection, apply to next character typed
                this.quill.format("background", value);
              }
            }
          },
          // Custom image handler
          image: function () {
            // Show a message that image uploads are in the attachments section
            toast.info(
              "Please use the Attachments section to upload images and files"
            );
          },
        },
      },
      clipboard: {
        matchVisual: false,
      },
    };

    // Initialize Quill editor
    quillRef.current = new Quill(editorElement, {
      modules: customModules,
      formats: formats || [
        "header",
        "bold",
        "italic",
        "underline",
        "strike",
        "list",
        "bullet",
        "ordered",
        "link",
        "image",
        "background",
        "color",
      ],
      placeholder: placeholder || "Start typing...",
      theme: "snow",
      readOnly: readOnly,
    });

    // Get the toolbar and add styling classes
    const toolbarElement = quillRef.current.getModule("toolbar").container;
    toolbarElement.classList.add("quill-custom-toolbar");
    toolbarElement.classList.add("lotus-theme");

    // Ensure the toolbar color and background controls are visible
    const colorPickers = toolbarElement.querySelectorAll(
      ".ql-color, .ql-background"
    );
    colorPickers.forEach((picker) => {
      picker.style.display = "inline-block";
      picker.style.visibility = "visible";

      // Fix for extra dropdowns by hiding unwanted elements
      const extraDropdowns = picker.querySelectorAll(
        ".ql-picker:not(.ql-expanded) + .ql-picker"
      );
      extraDropdowns.forEach((dropdown) => {
        dropdown.style.display = "none";
      });

      // Preserve selection when clicking on color/background dropdowns
      const pickerLabel = picker.querySelector(".ql-picker-label");
      if (pickerLabel) {
        // Create a better approach that preserves selection while allowing dropdowns to open
        const handleMouseDown = (e) => {
          // Store the current selection
          const selection = quillRef.current.getSelection();

          // Let the click event proceed normally to open the dropdown
          // But use setTimeout to restore selection after Quill processes the click
          if (selection && selection.length > 0) {
            setTimeout(() => {
              quillRef.current.setSelection(selection);
            }, 0);
          }
        };

        // First remove any existing listeners to avoid duplicates
        pickerLabel.removeEventListener("mousedown", handleMouseDown);

        // Add the new event listener
        pickerLabel.addEventListener("mousedown", handleMouseDown);
      }
    });

    // Set initial content if available
    if (value) {
      isUpdatingRef.current = true;

      // Use Quill's clipboard API to properly handle formatted HTML
      quillRef.current.clipboard.dangerouslyPasteHTML(value);

      // Register attributors for highlighting after content is loaded
      const BackgroundStyle = Quill.import("attributors/style/background");
      const ColorStyle = Quill.import("attributors/style/color");

      // Make sure these formats are enabled for this editor instance
      quillRef.current.format("background", true);
      quillRef.current.format("color", true);

      // Short delay to ensure formats are applied
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    }

    // Track selection changes
    quillRef.current.on("selection-change", function (range) {
      if (range && !isUpdatingRef.current) {
        selectionRef.current = range;

        // Mark user as typing when they select text
        isTypingRef.current = true;

        // Clear any existing typing timer
        if (typingTimerRef.current) {
          clearTimeout(typingTimerRef.current);
        }

        // Set typing status to false after 2 seconds of inactivity
        typingTimerRef.current = setTimeout(() => {
          isTypingRef.current = false;
        }, 2000);
      }
    });

    // Handle content changes
    quillRef.current.on("text-change", function (delta, oldDelta, source) {
      if (source === "user" && onChange && !isUpdatingRef.current) {
        // Update typing status
        isTypingRef.current = true;

        // Update selection reference
        const currentSelection = quillRef.current.getSelection();
        if (currentSelection) {
          selectionRef.current = currentSelection;
        }

        // Clear existing typing timer
        if (typingTimerRef.current) {
          clearTimeout(typingTimerRef.current);
        }

        // Set typing status to false after 2 seconds of inactivity
        typingTimerRef.current = setTimeout(() => {
          isTypingRef.current = false;
        }, 2000);

        // Get the HTML content with formatting
        const formattedHtml = quillRef.current.root.innerHTML;
        onChange(formattedHtml);
      }
    });

    // Cleanup on unmount
    return cleanupQuill;
  }, [readOnly, placeholder]); // Re-initialize on these prop changes

  // Handle external content updates with cursor position preservation
  useEffect(() => {
    if (quillRef.current && value !== undefined) {
      const editorContent = quillRef.current.root.innerHTML;

      // Only update if content changed and not already updating
      if (editorContent !== value && !isUpdatingRef.current) {
        // Don't update while user is actively typing
        if (isTypingRef.current) {
          console.log("Skipping update while user is typing");
          return;
        }

        console.log("Updating editor content from props");

        // Set the updating flag
        isUpdatingRef.current = true;

        // Save selection
        const currentSelection = quillRef.current.getSelection();
        const savedSelection = currentSelection || selectionRef.current;

        // Update content using Quill's API properly
        quillRef.current.clipboard.dangerouslyPasteHTML(value);

        // Restore selection after update
        setTimeout(() => {
          if (savedSelection) {
            try {
              quillRef.current.setSelection(savedSelection);
            } catch (err) {
              console.warn("Could not restore selection", err);
            }
          }
          isUpdatingRef.current = false;
        }, 100);
      }
    }
  }, [value]);

  return (
    <div
      className={`quill-editor-container lotus-theme ${
        connectionStatus !== "connected" ? "offline" : ""
      }`}
    >
      {/* Editor container for Quill */}
      <div ref={editorContainerRef} className="custom-quill-editor" />
    </div>
  );
};

export default QuillEditor;
